import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Typography,
} from '@mui/material';
import type { V2MultiSelectElement } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';
import { refineFonts } from '@/Theming/refineFonts';

export function MultiSelectElement({
	element,
	value,
	errors,
	disabled,
	onChange,
}: V2ElementRendererProps<V2MultiSelectElement>) {
	const selected = Array.isArray(value) ? (value as string[]) : [];
	const errorMessage = errors[0]?.message;
	const hasError = errors.length > 0;
	// Stable id so the checkbox group describes itself via its error helper text (RND-4285).
	const errId = `hrr-error-${element.elementId}`;

	const toggle = (key: string, checked: boolean) => {
		const next = checked ? [...new Set([...selected, key])] : selected.filter((k) => k !== key);
		onChange(next);
	};

	return (
		<FormControl error={hasError} required={element.required} disabled={disabled} component="fieldset">
			<FormLabel component="legend" sx={{ ...refineFonts.smallRegular, color: 'text.secondary' }}>
				{element.label}
			</FormLabel>
			<FormGroup
				aria-invalid={hasError}
				aria-describedby={hasError ? errId : undefined}
				data-cy={`hrr-element-${element.elementId}`}
				data-element-type="multiSelect">
				{element.options.map((opt) => (
					<FormControlLabel
						key={opt.key}
						control={
							<Checkbox
								checked={selected.includes(opt.key)}
								onChange={(e) => toggle(opt.key, e.target.checked)}
								data-cy={`hrr-option-${element.elementId}-${opt.key}`}
							/>
						}
						label={<Typography sx={{ ...refineFonts.mediumRegular }}>{opt.label}</Typography>}
					/>
				))}
			</FormGroup>
			{(errorMessage || element.description) && (
				<FormHelperText id={errId}>{errorMessage ?? element.description}</FormHelperText>
			)}
		</FormControl>
	);
}
